import { BubbleMenu, Editor } from '@tiptap/react'
import { PAGE_UI_ID } from 'client/constants/editorIds'
import React, { useEffect, useLayoutEffect, useState } from 'react'
import { TiptapEditorUpdatePayload } from '..'
import { LinkIcon } from '../ui/icons/link-icon'
import { ToolbarButtonUi } from '../ui/toolbar-button-ui'
import { ToolbarEditorUi } from '../ui/toolbar-ui'
import { ColorPickerControls } from './color-picker/color-picker-controls'
import { HeadlineControls } from './headline-controls'
import { InlineStyleControls } from './inline-style-controls'
import { LinkToolbar } from './link-toolbar/link-toolbar'

interface ToolbarProps {
  editor: Editor
  enableHeadings: boolean
  update: (payload: Partial<TiptapEditorUpdatePayload>) => void
  isBlogPage: boolean
}
export const Toolbar = ({
  update,
  editor,
  enableHeadings,
  isBlogPage,
}: ToolbarProps) => {
  const [showLinkToolbar, setShowLinkToolbar] = useState(
    !!editor.getAttributes('link').href,
  )
  const createOrEditLink = (event: React.SyntheticEvent) => {
    event.preventDefault()
    setShowLinkToolbar(!showLinkToolbar)
  }

  useEffect(() => {
    const changeShowLinkToolbar = (event: MouseEvent) => {
      const { state } = editor
      const { from, to } = state.selection

      const isTextSelected = from !== to

      const { href, popupid } = editor.getAttributes('link')
      if (!isTextSelected && (href || popupid)) {
        const target = event.target as Element
        const selectedLink = target.closest('a')

        if (selectedLink) {
          const range = document.createRange()
          const selection = window.getSelection()

          selection?.removeAllRanges()

          range.selectNodeContents(selectedLink)
          selection?.addRange(range)
        }

        setShowLinkToolbar(true)
      } else {
        setShowLinkToolbar(false)
      }
    }

    editor.view.dom.addEventListener('click', changeShowLinkToolbar)
    return () => {
      editor.view.dom.removeEventListener('click', changeShowLinkToolbar)
    }
  }, [])

  useLayoutEffect(() => {
    requestAnimationFrame(() => {
      editor.commands.focus()
      editor.view.dispatch(editor.state.tr.setMeta('bubble-refresh', true))
    })
  }, [showLinkToolbar])
  return (
    <BubbleMenu
      key={showLinkToolbar ? 'toolbarWithLink' : 'defaultToolbar'}
      className="flex w-full max-w-[486px] flex-col"
      editor={editor}
      tippyOptions={{
        placement: 'bottom',
        offset: [0, 10],
        popperOptions: {
          strategy: 'fixed',
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: document.getElementById(PAGE_UI_ID) as HTMLElement,
              },
            },
            {
              name: 'computeStyles',
              options: {
                adaptive: false,
                gpuAcceleration: false,
              },
            },
          ],
        },
      }}
    >
      <div className="flex w-full flex-wrap gap-0.5 p-2">
        <ToolbarEditorUi>
          {enableHeadings && (
            <HeadlineControls update={update} editor={editor} />
          )}
          <InlineStyleControls editor={editor} />
          <ColorPickerControls editor={editor} />
          <ToolbarButtonUi onClick={createOrEditLink}>
            <LinkIcon />
          </ToolbarButtonUi>
        </ToolbarEditorUi>

        {showLinkToolbar && (
          <LinkToolbar
            editor={editor}
            close={() => setShowLinkToolbar(false)}
            isBlogPage={isBlogPage}
          />
        )}
      </div>
    </BubbleMenu>
  )
}
